import {
  Client, IFile, IFileType, IFileStatus, ILabel, BrowserZmlClient, validateImage, IUploadFileOptions,
  IAnalyticsSummary, IAnalyticsViews, IAnalyticsReferrers
} from "@zappar/zw-api-client";
import { BaseProvider } from "../base/BaseProvider";
import { IAsset, SearchParams, AssetMetadata } from "../base/types";
import { ZapworksAsset, ImageAsset, Model3DAsset, AudioAsset, VideoAsset, Image360Asset, Video360Asset } from "./assets";
import { MediaType } from "../../types";
import { MAX_IMAGE_SIZE, MAX_MODEL_SIZE, MAX_VIDEO_SIZE, MAX_AUDIO_SIZE, getMediaTypeFromExtension } from "../../utils";

/**
 * Zapworks Provider
 *
 * Provides read/write access to user's Zapworks Media Library.
 * This is the only provider that supports uploading and deleting assets.
 */
export class ZapworksProvider extends BaseProvider {
  private client: BrowserZmlClient;

  constructor(apiClient: Client) {
    super({
      id: 'zapworks',
      name: 'zapworks',
      displayName: 'Zapworks',
      description: 'Your personal Zapworks media library',
      baseUrl: '', // Not used for Zapworks as it uses the API client
      capabilities: {
        read: true,
        write: true,
        delete: true,
        supportedTypes: [MediaType.Image, MediaType.Video, MediaType.Audio, MediaType.Model3D, MediaType.Image360, MediaType.Video360]
      }
    });

    this.client = apiClient.zml;
  }

  /**
   * Map MediaType to Zapworks IFileType
   */
  mapTypeToZapworksType(mediaType: MediaType): IFileType {
    switch (mediaType) {
      case MediaType.Image:
        return IFileType.Image;
      case MediaType.Video:
        return IFileType.StreamingVideo;
      case MediaType.Audio:
        return IFileType.Audio;
      case MediaType.Model3D:
        return IFileType.Model3D;
      case MediaType.Image360:
        return IFileType.Image360;
      case MediaType.Video360:
        return IFileType.Video360;
      default:
        throw new Error(`Unsupported media type for Zapworks: ${mediaType}`);
    }
  }

  /**
   * Search for assets in the Zapworks library
   */
  async search(params: SearchParams): Promise<IAsset[]> {
    const { assets } = await this.searchWithCount(params);
    return assets;
  }

  /**
   * Search for assets and return total count
   */
  async searchWithCount(params: SearchParams): Promise<{ assets: IAsset[]; count: number; next?: string }> {
    const fileTypes = (params.types || this.getSupportedTypes()).map(type => this.mapTypeToZapworksType(type));

    // If source is 'bin', fetch both user and workspace bins
    if (params.source === 'bin') {
      let files: any[] = [];
      let count = 0;

      // Always fetch user bin
      try {
        const userBin = await this.client.listBin();
        files = files.concat(userBin.results);
        count += userBin.count;
      } catch (err) {
        console.error('Failed to fetch user bin:', err);
      }

      // Fetch workspace bin if workspaceId is provided
      if (params.workspaceId) {
        try {
          const workspaceBin = await this.client.listBin(params.workspaceId);
          files = files.concat(workspaceBin.results);
          count += workspaceBin.count;
        } catch (err) {
          console.error('Failed to fetch workspace bin:', err);
        }
      }

      // getBin doesn't filter by type natively, so we do it client-side
      if (fileTypes.length > 0) {
        files = files.filter(f => fileTypes.includes(f.type));
        count = files.length;
      }

      return {
        assets: files.map(file => this.wrapAsset(file)),
        count,
      };
    }

    // If source is 'zappar', ignore the source filter and search access=public
    if (params.source === 'zappar') {
      params.source = undefined;
      params.access = 'public';
    }

    const response = await this.client.search({
      fileTypes,
      source: params.source,
      access: params.access,
      page: params.page,
      query: params.query,
      limit: params.limit,
      sort: params.sort,
      status: params.status as IFileStatus,
      workspaceId: params.workspaceId,
      labels: params.label as string[],
      workflowStatus: params.wfstatus as string[],
      publiclinkExperience: params.publiclinkExperience as string[],
    });

    // Wrap the results in appropriate asset classes
    return {
      assets: response.results.map(file => this.wrapAsset(file)),
      count: response.count,
      next: response.next,
    };
  }

  /**
   * Get a specific asset by ID
   */
  async getAssetById(id: string, workspaceId?: string): Promise<IAsset> {
    const file = await this.client.getFileById(id, workspaceId);
    return this.wrapAsset(file);
  }

  /**
   * Upload a new asset to Zapworks
   */
  async upload(
    file: File,
    metadata: AssetMetadata,
    onProgress?: (progress: number) => void
  ): Promise<IAsset> {
    // Determine the file type based on the file's MIME type or metadata
    const fileType = this.determineFileType(file, metadata);

    await this.validateFile(file, fileType);

    const workspaceId = metadata.workspaceId as string | undefined;

    // uploadFilesOfType handles the full flow:
    // 1. S3 upload with progress
    // 2. POST /zml/ to register the file and get a real IFile with ID
    // 3. Kicks off background job processing
    const { files } = await this.client.uploadFilesOfType(
      [file],
      fileType,
      (progress) => onProgress?.(progress),
      workspaceId,
    );

    if (!files.length) throw new Error('Upload failed: no file returned');

    const uploadedFile = files[0];

    // Apply custom caption if provided and different from the filename
    const caption = metadata.caption || metadata.name;
    if (caption && caption !== file.name) {
      await this.client.updateFile(uploadedFile.id, { caption }, workspaceId);
    }

    if (metadata.workflowStatus !== undefined) {
      await this.client.updateFile(uploadedFile.id, { workflowStatusId: metadata.workflowStatus }, workspaceId);
    }

    return this.wrapAsset(uploadedFile);
  }

  private async validateFile(file: File, fileType: IFileType): Promise<void> {
    const maxSizes: Partial<Record<IFileType, number>> = {
      [IFileType.Image]: MAX_IMAGE_SIZE,
      [IFileType.StreamingVideo]: MAX_VIDEO_SIZE,
      [IFileType.Audio]: MAX_AUDIO_SIZE,
      [IFileType.Model3D]: MAX_MODEL_SIZE,
    };
    const maxSize = maxSizes[fileType];
    if (maxSize && file.size > maxSize) {
      throw new Error(`Maximum file size (${parseInt((maxSize / 1024 / 1024).toString())}MB) exceeded.`);
    }

    if (fileType === IFileType.Image) {
      const valid = await validateImage(file, maxSizes[IFileType.Image]);
      if (!valid) throw new Error('Invalid image file.');
    }
  }

  /**
   * Delete an asset from Zapworks
   */
  async delete(assetId: string, workspaceId?: string): Promise<void> {
    await this.client.moveToBin([assetId], workspaceId);
  }

  async restore(assetId: string, workspaceId?: string): Promise<void> {
    await this.client.restoreFile(assetId, workspaceId);
  }

  // ==================== File Updates ====================

  async updateFile(assetId: string, data: IUploadFileOptions, workspaceId?: string) {
    const file = await this.client.updateFile(assetId, data, workspaceId);
    return this.wrapAsset(file);
  }

  async updateFileLabels(assetId: string, labelsUid: string[], workspaceId: string) {
    const file = await this.client.setFileLabels(workspaceId, assetId, labelsUid);
    return this.wrapAsset(file);
  }

  async updateFileFolder(assetId: string, folderId: string | null, workspaceId?: string) {
    return this.updateFile(assetId, { folderId }, workspaceId);
  }

  async updateFileCaption(assetId: string, caption: string, workspaceId?: string) {
    return this.updateFile(assetId, { caption }, workspaceId);
  }

  async updateWorkflowStatus(assetId: string, workflowStatusId: string | null, workspaceId?: string) {
    return this.updateFile(assetId, { workflowStatusId }, workspaceId);
  }

  // ==================== File Status & History ====================

  async getFileHistory(workspaceId: string, assetId: string) {
    return this.client.getHistory(workspaceId, assetId);
  }

  async uploadNewVersion(file: IFile, newFile: File) {
    const updatedFile = await this.client.uploadNewVersion(file, newFile);
    return this.wrapAsset(updatedFile);
  }

  // ==================== Labels ====================

  async getLabels(workspaceId: string): Promise<ILabel[]> {
    const response = await this.client.listLabels(workspaceId);
    return response.results.flat();
  }

  // async getLabel(uid: string): Promise<ILabel> {
  //   return this.client.getLabel(uid);
  // }

  async createLabel(workspaceId: string, name: string, color?: string) {
    return this.client.createLabel(workspaceId, name, color);
  }

  async updateLabel(workspaceId: string, uid: string, name: string, color?: string): Promise<ILabel> {
    return this.client.updateLabel(workspaceId, uid, name, color);
  }

  async deleteLabel(workspaceId: string, uid: string): Promise<void> {
    await this.client.deleteLabel(workspaceId, uid);
  }

  // ==================== Workflow Statuses ====================

  async listWorkflowStatus(workspaceId: string) {
    return this.client.listWorkflowStatus(workspaceId);
  }

  // ==================== Analytics ====================

  async getAnalyticsSummary(workspaceId: string, mediafileId: string): Promise<IAnalyticsSummary> {
    return this.client.getAnalyticsSummary(workspaceId, mediafileId);
  }

  async getAnalytics(workspaceId: string, mediafileId: string, params?: { start?: string; end?: string }): Promise<IAnalyticsViews> {
    return this.client.getAnalytics(workspaceId, mediafileId, params);
  }

  async getAnalyticsReferrers(workspaceId: string, mediafileId: string, params?: { start?: string; end?: string }): Promise<IAnalyticsReferrers> {
    return this.client.getAnalyticsReferrers(workspaceId, mediafileId, params);
  }

  // ==================== Additional Methods ====================
  async moveFilesToWorkspace(workspaceId: string, fileIds: string[]) {
    await this.client.moveFilesToWorkspace(workspaceId, fileIds);
  }

  /**
   * Import a public media file into a workspace. The server deep-copies the
   * underlying S3 objects and returns a new workspace-owned MediaFile.
   */
  async copyFileToWorkspace(workspaceId: string, fileId: string): Promise<IAsset> {
    const file = await this.client.copyToWorkspace(workspaceId, fileId);
    return this.wrapAsset(file);
  }

  /**
   * Wrap an IFile in the appropriate asset class
   */
  private wrapAsset(file: IFile): IAsset {
    switch (file.type) {
      case IFileType.Image:
        return new ImageAsset(this.client, file);
      case IFileType.Model3D:
        return new Model3DAsset(this.client, file);
      case IFileType.StreamingVideo:
        return new VideoAsset(this.client, file);
      case IFileType.Audio:
        return new AudioAsset(this.client, file);
      case IFileType.Image360:
        return new Image360Asset(this.client, file);
      case IFileType.Video360:
        return new Video360Asset(this.client, file);
      default:
        // Fall back to generic ZapworksAsset for unsupported types
        return new ZapworksAsset(this.client, file);
    }
  }

  /**
   * Determine the IFileType from metadata or the file's MIME type.
   *
   * When metadata.type is a MediaType string we map it through
   * mapTypeToZapworksType so the enum values stay consistent.
   */
  private determineFileType(file: File, metadata: AssetMetadata): IFileType {
    // Use metadata type if provided and map it correctly
    if (metadata.type) {
      // Check if metadata.type is a MediaType value (from our enum)
      const mediaTypeValues = Object.values(MediaType) as string[];
      if (mediaTypeValues.includes(metadata.type)) {
        return this.mapTypeToZapworksType(metadata.type as MediaType);
      }
      // Otherwise assume it's already an IFileType
      return metadata.type as IFileType;
    }

    // Determine from MIME type
    const mimeType = file.type.toLowerCase();

    if (mimeType.startsWith('image/')) {
      return IFileType.Image;
    } else if (mimeType.startsWith('video/')) {
      return IFileType.StreamingVideo;
    } else if (mimeType.startsWith('audio/')) {
      return IFileType.Audio;
    } else if (mimeType.includes('gltf') || mimeType.includes('glb') || mimeType.includes('model')) {
      return IFileType.Model3D;
    }

    // Fallback: check file extension (3D files often have application/octet-stream or empty MIME)
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const extType = getMediaTypeFromExtension(extension);
    if (extType) {
      return this.mapTypeToZapworksType(extType);
    }

    // Default to image
    return IFileType.Image;
  }
}
